import { clsx } from 'clsx';
import type { ButtonHTMLAttributes } from 'react';
import { forwardRef } from 'react';

/**
 * Four variants, three sizes, and every colour a SEMANTIC token — never a hex, never a palette
 * primitive. The ESLint rule enforces it; the reason is that the commissioner is expected to want
 * palette changes once she sees real screens, and that has to stay a single-file edit.
 *
 * NAVY IS THE GROUND; RED IS THE ACTION. `primary` is `--signal`, not `--deep`.
 *
 * This inverts the previous palette, where the deep green-grey was both the panel fill and the
 * primary button. In Navy & Chalk the navy fills heroes, rails and banners — so a navy button on a
 * navy panel is invisible, which is the whole reason UI-UX-SPEC §2 made the change. Navy is the
 * secondary now, as an outline rather than a fill.
 *
 * Consequence for the scarcity rule: "one signal thing per screen" counts elements AT REST. A
 * primary button is an action, not an accent, and does not spend the budget. What still counts is a
 * chip, a tinted row, a left bar, a highlighted seat.
 *
 * `--signal` carries `--on-signal` text at 4.99:1 — a near-white rather than pure white, which
 * would land on 4.5 exactly and leave nothing for a rounding argument.
 *
 * Heights are the spec's: 48px on the public site, 44px in-app, 36px inline. They are not a style
 * choice — 44px is the smallest comfortable touch target, and a director taps these on a phone
 * while standing up in a room.
 */
/**
 * `accent` is gone rather than kept as an alias. It was the scarce clay button; primary IS that
 * colour now, so the two would be the same control under two names — which is how a design system
 * ends up with a decision nobody can explain. It had no call sites.
 */
export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'secondaryOnDeep'
  | 'destructive'
  | 'quiet'
  | 'plain';
export type ButtonSize = 'public' | 'app' | 'inline';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
}

const SIZES: Record<ButtonSize, string> = {
  public: 'h-12 px-6 text-[15px]',
  app: 'h-11 px-5 text-sm',
  inline: 'h-9 px-3.5 text-[13px]',
};

const VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-signal text-on-signal hover:bg-signal-hover',
  /**
   * TRANSPARENT, not a white fill. It has to sit on chalk, on a card and on sand without carrying
   * its own ground around — a white slab on an inset panel reads as a second card.
   */
  /* `control-border`, not `stroke`: this edge IS the button, and WCAG 1.4.11 wants 3:1 of it. */
  secondary: 'border border-control-border bg-transparent text-ink hover:bg-sand',
  /**
   * The same control on a navy panel. `--stroke` is a warm grey and disappears on navy at 1.3:1;
   * `--on-deep-3` is the border that is actually visible there, at 4.85:1.
   */
  secondaryOnDeep: 'border border-on-deep-3 bg-transparent text-on-deep hover:bg-deep-hover',
  /**
   * Transparent with rust text, not a solid red slab. A destructive action should read as serious
   * without shouting — a filled red button next to a filled primary makes the page look alarmed
   * about everything. Now that primary is red, this matters more, not less: the two must not be
   * confusable, which is why `--rust` sits so much deeper than `--signal`.
   */
  destructive: 'border border-rust/35 bg-transparent text-rust hover:bg-tint-rust',
  /**
   * ⚠ AN UNDERLINE MEANS "THIS GOES SOMEWHERE". IT DOES NOT MEAN "THIS DOES SOMETHING".
   *
   * Owner's ruling, 2026-09-19, and it narrows this variant sharply. `quiet` was described as "a
   * link that happens to be a button" and was used for any lesser ACTION — 47 call sites. Beside it,
   * twenty files hand-rolled `underline underline-offset-4` on real navigation. So an underlined
   * word on this platform might take you somewhere or might change your data, and nothing about it
   * said which. His words: "we don't seem to be consistent in the UI."
   *
   * THE RULE NOW:
   *
   *   underlined text  → NAVIGATION. A `<Link>`, or a button that only navigates.
   *   a button shape   → AN ACTION. However small, however secondary.
   *
   * So `quiet` is for a control that NAVIGATES and must not compete with the primary — "View full
   * standings", "All leagues", "Cancel" out of a form. An action that writes anything takes
   * `secondary`, or `destructive` if it takes something away.
   *
   * It stays INK rather than taking the link red. The brand board names `--signal-d` for links, and
   * that is scoped to links inside PROSE — a coloured word in a sentence has to announce itself. A
   * quiet control usually sits directly beneath the red primary, and two reds stacked is how a page
   * ends up with no primary action at all. (Decided 2026-08-29 with the owner.)
   */
  quiet: 'text-ink-2 underline underline-offset-4 hover:text-ink',
  /**
   * A TABLE ROW'S ACTIONS — Invite, Add to Roster, Remove (owner, 2026-10-16, after seeing how
   * Stripe, Shopify and Linear do it): plain text, no border and NO underline, so a row of them stays
   * calm and none of them reads as a link. Add `hover:text-rust hover:border-rust/35` for one that
   * takes something away. Only on rows; everywhere else an action is `secondary`.
   */
  plain:
    'min-h-9 rounded-md border border-transparent px-2.5 text-[14px] text-ink-3 group-hover:text-ink-2 hover:bg-paper focus-visible:outline focus-visible:outline-2 focus-visible:outline-deep',
};

/*
  THE WHOLE RULE — owner, 2026-09-19, completed 2026-10-16 ("a mix of use, without any real logic"):

    an underlined word   → GOES SOMEWHERE to look at something: View…, Back, Open…, a name.
                           Use LINK_CLASS on a <Link>/<a>, or `quiet` on a button that only navigates.
    a button shape       → DOES SOMETHING or STARTS A TASK: save, send, add, create, print, change,
                           remove — even when the task opens a new page or a PDF.
    one `primary`        → the screen's main task. Never repeated down a list of rows.
    `secondary`          → every other action, including Cancel beside a Save (same size as it).
    `destructive`        → anything that takes something away, and the button that confirms it.
    `plain`              → the actions on a table row, and nowhere else.

  A link that must look like a button (Print, a CTA) takes `buttonClass(...)`.
*/

/** The button look for an <a> or <Link>: Print score sheet, Print table tents. */
export function buttonClass(variant: ButtonVariant = 'primary', size: ButtonSize = 'app'): string {
  return clsx(
    'inline-flex items-center justify-center gap-2 rounded-md font-medium transition-colors',
    variant === 'quiet' || variant === 'plain' ? 'h-auto' : SIZES[size],
    variant === 'quiet' ? 'px-0' : '',
    VARIANTS[variant],
  );
}

/** A real link — it goes somewhere. Underlined, ink, never red outside prose. */
export const LINK_CLASS =
  'text-ink-2 underline underline-offset-4 hover:text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-deep';

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant = 'primary', size = 'app', type = 'button', ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      /** Explicit, because an unlabelled button inside a form submits it. That has bitten. */
      type={type}
      className={clsx(
        'inline-flex items-center justify-center gap-2 rounded-md font-medium transition-colors',
        'disabled:cursor-not-allowed disabled:opacity-55',
        variant === 'quiet' ? 'h-auto px-0' : variant === 'plain' ? '' : SIZES[size],
        VARIANTS[variant],
        className,
      )}
      {...props}
    />
  );
});
